import { useEffect, useState } from 'react'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQueryList } from '@percent/utility'
import { Account } from '@percent/domain/iam'
import { GetAccountsProps } from '@percent/admin-dashboard/api/actions/accounts/accounts.types'

export const ACCOUNT_SEARCH_MIN_LENGTH = 2
const DEBOUNCE_MS = 700

/**
 * Debounced, cross-account-type search used by the sidebar account search.
 * Deliberately omits the `type` filter so the query spans internal, organisation
 * and partner accounts (unlike the Members tab, which pins `type: internal`).
 */
export function useAccountSearch() {
  const { accountService } = useServices()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [settledQuery, setSettledQuery] = useState<string | null>(null)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [query])

  const enabled = debouncedQuery.length >= ACCOUNT_SEARCH_MIN_LENGTH

  // `useQueryList` appends the params (`{ search: debouncedQuery }`) to the query
  // key internally, so each distinct query is cached under its own key — the
  // static base key here does not conflate results across searches.
  const [{ data, isFetching }] = useQueryList<GetAccountsProps, Account>(
    ['/accounts/search'],
    accountService.getAccounts,
    { search: debouncedQuery },
    { enabled }
  )

  // Record the query whose results have actually landed. `useQueryList` keeps the
  // previous query's data while the next loads (placeholderData: keepPreviousData)
  // and there's a render where the debounce has updated but the fetch hasn't
  // registered as in-flight yet — so gate on "settled for this exact query"
  // rather than `isFetching`, to avoid flashing stale results or a premature
  // "no results".
  useEffect(() => {
    if (enabled && !isFetching) {
      setSettledQuery(debouncedQuery)
    }
  }, [enabled, isFetching, debouncedQuery])

  const currentQuery = query.trim()
  const hasMinLength = currentQuery.length >= ACCOUNT_SEARCH_MIN_LENGTH
  const isSettledForCurrentQuery =
    hasMinLength && currentQuery === debouncedQuery && settledQuery === debouncedQuery && !isFetching

  return {
    query,
    setQuery,
    results: isSettledForCurrentQuery && data ? data : [],
    isLoading: hasMinLength && !isSettledForCurrentQuery
  }
}
